import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getAssistants, getCurrentUserRoles, getGroups } from '../services/api';

const UserSessionContext = createContext(null);

function buildGroupsMap(groupsData) {
  const map = {};
  if (Array.isArray(groupsData)) {
    groupsData.forEach((g) => {
      map[g.id] = g.name;
    });
  }
  return map;
}

export function UserSessionProvider({ children }) {
  const { keycloak, initialized } = useKeycloak();
  const [roles, setRoles] = useState(null);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [userAssistants, setUserAssistants] = useState([]);
  const [platformAssistants, setPlatformAssistants] = useState([]);
  const [assistantsLoaded, setAssistantsLoaded] = useState(false);
  const [groupsMap, setGroupsMap] = useState({});
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const assistantsInflightRef = useRef(null);

  const isAdmin = Boolean(roles?.is_admin);

  const fetchAssistants = useCallback(async (admin) => {
    if (assistantsInflightRef.current) return assistantsInflightRef.current;
    const promise = (async () => {
      try {
        const userPromise = getAssistants(keycloak, false);
        const platformPromise = admin ? getAssistants(keycloak, true) : Promise.resolve([]);
        const [userList, platList] = await Promise.all([userPromise, platformPromise]);
        return {
          user: Array.isArray(userList) ? userList : [],
          platform: Array.isArray(platList) ? platList : [],
        };
      } finally {
        assistantsInflightRef.current = null;
      }
    })();
    assistantsInflightRef.current = promise;
    return promise;
  }, [keycloak]);

  const loadSession = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated || !keycloak.token) {
      setRoles(null);
      setRolesLoaded(false);
      setUserAssistants([]);
      setPlatformAssistants([]);
      setAssistantsLoaded(false);
      setGroupsMap({});
      setGroupsLoaded(false);
      return;
    }

    setRolesLoaded(false);
    setAssistantsLoaded(false);
    setGroupsLoaded(false);
    try {
      const rolesData = await getCurrentUserRoles(keycloak);
      const admin = Boolean(rolesData?.is_admin);
      setRoles(rolesData);
      setRolesLoaded(true);

      const [{ user, platform }, groupsData] = await Promise.all([
        fetchAssistants(admin),
        getGroups(keycloak),
      ]);
      setUserAssistants(user);
      setPlatformAssistants(platform);
      setAssistantsLoaded(true);
      setGroupsMap(buildGroupsMap(groupsData));
      setGroupsLoaded(true);
    } catch (e) {
      console.error('Failed to load user session:', e);
      setRoles(null);
      setRolesLoaded(true);
      setUserAssistants([]);
      setPlatformAssistants([]);
      setAssistantsLoaded(true);
      setGroupsMap({});
      setGroupsLoaded(true);
    }
  }, [initialized, keycloak, fetchAssistants]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const refreshRoles = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    try {
      const rolesData = await getCurrentUserRoles(keycloak);
      setRoles(rolesData);
      setRolesLoaded(true);
    } catch (e) {
      console.error('Failed to refresh roles:', e);
    }
  }, [keycloak]);

  const refreshAssistants = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    try {
      const admin = Boolean(roles?.is_admin);
      const { user, platform } = await fetchAssistants(admin);
      setUserAssistants(user);
      setPlatformAssistants(platform);
      setAssistantsLoaded(true);
    } catch (e) {
      console.error('Failed to refresh assistants:', e);
    }
  }, [keycloak, roles?.is_admin, fetchAssistants]);

  const refreshGroups = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    try {
      const groupsData = await getGroups(keycloak);
      setGroupsMap(buildGroupsMap(groupsData));
      setGroupsLoaded(true);
    } catch (e) {
      console.error('Failed to refresh groups:', e);
    }
  }, [keycloak]);

  const refreshSession = useCallback(async () => {
    await loadSession();
  }, [loadSession]);

  const value = useMemo(
    () => ({
      roles,
      isAdmin,
      rolesLoaded,
      userAssistants,
      platformAssistants,
      assistantsLoaded,
      groupsMap,
      groupsLoaded,
      refreshRoles,
      refreshAssistants,
      refreshGroups,
      refreshSession,
    }),
    [
      roles,
      isAdmin,
      rolesLoaded,
      userAssistants,
      platformAssistants,
      assistantsLoaded,
      groupsMap,
      groupsLoaded,
      refreshRoles,
      refreshAssistants,
      refreshGroups,
      refreshSession,
    ]
  );

  return (
    <UserSessionContext.Provider value={value}>{children}</UserSessionContext.Provider>
  );
}

export function useUserSession() {
  const ctx = useContext(UserSessionContext);
  if (!ctx) {
    throw new Error('useUserSession must be used within UserSessionProvider');
  }
  return ctx;
}
